import React from 'react';

import MapContent from 'app/components/DeclarativeMap/MapContent';
import colors from 'app/styles/colors.json';
import * as mapUtils from 'app/utils/mapUtils';

import {useGrid} from './GridContext';
import GridDelightOverlay from './GridDelightOverlay';
import {colRowToCellId} from './gridGeometry';
import {useGridCamera, useGridKeyboardNav} from './gridHooks';

interface Props {
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  /** When true (default), this map instance owns camera navigation and the
   *  keyboard-nav listener. Set to false on the before-map (camera follower) in
   *  compare mode so only the leader drives the camera and the listener isn't
   *  registered twice. */
  isPrimaryMap?: boolean;
}

const SOURCE_ID = 'property-grid-source';
const FILL_LAYER_ID = 'property-grid-fill';
const LINE_LAYER_ID = 'property-grid-line';

const REVIEWED_SOURCE_ID = 'property-grid-reviewed-source';
const REVIEWED_FILL_LAYER_ID = 'property-grid-reviewed-fill';

// Fully transparent fill. Only present so click/hover events hit-test against
// the cell shape.
const FILL_LAYER: mapboxgl.FillLayerSpecification = {
  id: FILL_LAYER_ID,
  type: 'fill',
  source: SOURCE_ID,
  paint: {'fill-color': 'transparent', 'fill-opacity': 0},
};

const LINE_LAYER: mapboxgl.LineLayerSpecification = {
  id: LINE_LAYER_ID,
  type: 'line',
  source: SOURCE_ID,
  paint: {'line-color': colors.white, 'line-width': 1, 'line-opacity': 0.7},
};

const REVIEWED_FILL_LAYER: mapboxgl.FillLayerSpecification = {
  id: REVIEWED_FILL_LAYER_ID,
  type: 'fill',
  source: REVIEWED_SOURCE_ID,
  paint: {'fill-color': colors.white, 'fill-opacity': 0.4},
};

const CLICKABLE_LAYERS: mapboxgl.AnyLayer[] = [FILL_LAYER];
const ALL_LAYERS: mapboxgl.LayerSpecification[] = [FILL_LAYER, LINE_LAYER];

const PropertyGrid: React.FunctionComponent<Props> = ({
  map,
  isMapLoaded,
  isPrimaryMap = true,
}) => {
  const {
    isEnabled,
    spec,
    featureCollection,
    existingCellIds,
    activeCellId,
    setActiveCellId,
    reviewedCellIds,
    toggleCellReviewed,
  } = useGrid();

  useGridCamera(map, spec, activeCellId, isPrimaryMap);
  useGridKeyboardNav(
    spec,
    existingCellIds,
    activeCellId,
    setActiveCellId,
    toggleCellReviewed,
    isEnabled,
    isPrimaryMap
  );

  mapUtils.useClickableFeatures(map, CLICKABLE_LAYERS, (feature) => {
    const props = feature.properties as {col: number; row: number} | null;
    if (!props) return;
    setActiveCellId(colRowToCellId(props.col, props.row));
  });

  const source = React.useMemo(
    () =>
      featureCollection && {
        id: SOURCE_ID,
        source: {type: 'geojson' as const, data: featureCollection},
      },
    [featureCollection]
  );

  const reviewedSource = React.useMemo(() => {
    if (!featureCollection || reviewedCellIds.size === 0) return null;
    const features = featureCollection.features.filter((f) =>
      reviewedCellIds.has(colRowToCellId(f.properties.col, f.properties.row))
    );
    return {
      id: REVIEWED_SOURCE_ID,
      source: {type: 'geojson' as const, data: {...featureCollection, features}},
    };
  }, [featureCollection, reviewedCellIds]);

  if (!isEnabled || !source) return null;

  return (
    <>
      <MapContent map={map} isMapLoaded={isMapLoaded} sources={[source]} layers={ALL_LAYERS} />
      {reviewedSource && (
        <MapContent
          map={map}
          isMapLoaded={isMapLoaded}
          sources={[reviewedSource]}
          layers={[REVIEWED_FILL_LAYER]}
        />
      )}
      <GridDelightOverlay />
    </>
  );
};

export default PropertyGrid;
